import { useEffect, useMemo, useState, useRef } from "react";
import { Package, Truck, Eye, X as XIcon, Check, Upload, Download, FileSpreadsheet, RefreshCw, Pencil } from "lucide-react";
import type { BulkShipmentRegisterResponse } from "../../../domain/types/orders";
import { Button } from "../../components/UI/Button";
import TextInput from "../../components/UI/TextInput";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Table from "../../components/UI/Table";
import Modal from "../../components/UI/Modal";
import Selection from "../../components/UI/Selection";
import { CompanyOrderRepositoryImpl } from "infrastructure/repositories/company/company-order-repository-impl";
import { CompanyOrderUsecase } from "domain/usecases/company/company-order-usecase";
import { useParams } from "react-router-dom";

const COURIER_ENUM_MAP: Record<string, string> = {
  "01": "COURIER_01",
  "04": "COURIER_04",
  "05": "COURIER_05",
  "06": "COURIER_06",
  "08": "COURIER_08",
  "11": "COURIER_11",
  "16": "COURIER_16",
  "17": "COURIER_17",
  "18": "COURIER_18",
  "20": "COURIER_20",
  "22": "COURIER_22",
  "23": "COURIER_23",
  "24": "COURIER_24",
  "32": "COURIER_32",
  "43": "COURIER_43",
  "44": "COURIER_44",
  "45": "COURIER_45",
  "46": "COURIER_46",
  "47": "COURIER_47",
  "53": "COURIER_53",
  "54": "COURIER_54",
  "71": "COURIER_71",
  "72": "COURIER_72",
  "73": "COURIER_73",
  "74": "COURIER_74",
  "75": "COURIER_75",
  "82": "COURIER_82",
  "85": "COURIER_85",
  "89": "COURIER_89",
  "90": "COURIER_90",
  "92": "COURIER_92",
  "94": "COURIER_94",
  "95": "COURIER_95",
  "100": "COURIER_100",
  "101": "COURIER_101",
  "104": "COURIER_104",
  "107": "COURIER_107",
  "112": "COURIER_112",
  "113": "COURIER_113",
  "118": "COURIER_118",
  "119": "COURIER_119",
  "120": "COURIER_120",
  "123": "COURIER_123",
  "125": "COURIER_125",
  "127": "COURIER_127",
  "129": "COURIER_129",
  "130": "COURIER_130",
  "131": "COURIER_131",
  "132": "COURIER_132",
  "134": "COURIER_134",
  "135": "COURIER_135",
  "136": "COURIER_136",
  "137": "COURIER_137",
  "138": "COURIER_138",
  "142": "COURIER_142",
  "143": "COURIER_143",
  "145": "COURIER_145",
  "148": "COURIER_148",
  "155": "COURIER_155",
  "156": "COURIER_156",
  "157": "COURIER_157",
  "159": "COURIER_159",
  "160": "COURIER_160",
  "163": "COURIER_163",
  "167": "COURIER_167",
  "168": "COURIER_168",
  "170": "COURIER_170",
  "171": "COURIER_171",
  "173": "COURIER_173",
  "175": "COURIER_175",
  "179": "COURIER_179",
  "183": "COURIER_183",
  "184": "COURIER_184",
  "186": "COURIER_186",
};

const COURIER_LABEL_MAP: Record<string, string> = {
  COURIER_01: "우체국택배",
  COURIER_04: "CJ대한통운",
  COURIER_05: "한진택배",
  COURIER_06: "로젠택배",
  COURIER_08: "롯데택배",
  COURIER_11: "일양로지스",
  COURIER_16: "한의사랑택배",
  COURIER_17: "천일택배",
  COURIER_18: "건영택배",
  COURIER_20: "한덱스",
  COURIER_22: "대신택배",
  COURIER_23: "경동택배",
  COURIER_24: "GS Postbox 택배",
  COURIER_32: "합동택배",
  COURIER_43: "애니트랙",
  COURIER_44: "SLX택배",
  COURIER_45: "우리택배(구호남택배)",
  COURIER_46: "CU편의점택배",
  COURIER_47: "우리한방택배",
  COURIER_53: "농협택배",
  COURIER_54: "홈픽택배",
  COURIER_71: "IK물류",
  COURIER_72: "성훈물류",
  COURIER_73: "CR로지텍",
  COURIER_74: "용마로지스",
  COURIER_75: "원더스퀵",
  COURIER_82: "컬리넥스트마일",
  COURIER_85: "풀앳홈",
  COURIER_89: "두발히어로",
  COURIER_90: "위니아딤채",
  COURIER_92: "지니고 당일배송",
  COURIER_94: "카카오 T 당일배송",
  COURIER_95: "트랙스로지스",
  COURIER_100: "나은물류",
  COURIER_101: "한샘서비스원",
  COURIER_104: "도도플렉스(dodoflex)",
  COURIER_107: "LG전자",
  COURIER_112: "1004홈",
  COURIER_113: "썬더히어로",
  COURIER_118: "롯데칠성",
  COURIER_119: "핑퐁",
  COURIER_120: "발렉스 특수물류",
  COURIER_123: "엔티엘피스",
  COURIER_125: "지케이글로벌",
  COURIER_127: "로지스팟",
  COURIER_129: "홈픽 오늘도착",
  COURIER_130: "로지스파트너",
  COURIER_131: "딜리래빗",
  COURIER_132: "지오피",
  COURIER_134: "에이치케이홀딩스",
  COURIER_135: "HTNS",
  COURIER_136: "케이제이티",
  COURIER_137: "더바오",
  COURIER_138: "라스트마일",
  COURIER_142: "탱고앤고",
  COURIER_143: "투데이",
  COURIER_145: "현대글로비스",
  COURIER_148: "ARGO(테크타카)",
  COURIER_155: "HY",
  COURIER_156: "유피로지스(제주)",
  COURIER_157: "반얀로지스틱스",
  COURIER_159: "삼다수 가정배송",
  COURIER_160: "와이드테크",
  COURIER_163: "위니온",
  COURIER_167: "딜리박스",
  COURIER_168: "이스트라",
  COURIER_170: "유피로지스",
  COURIER_171: "올인닷컴",
  COURIER_173: "물류대장(LCS)",
  COURIER_175: "풀무원샘물㈜",
  COURIER_179: "SLO(모든)",
  COURIER_183: "바로스",
  COURIER_184: "JCLS",
  COURIER_186: "레터스",
};

export type Order = {
  id: number;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  status: OrderStatus;
  orderDate: string;
  updatedAt: string;
  memo?: string;
  optionsText?: string[];
  files?: string;
  trackingNumber?: string;
  courier?: string;
  trackingHistory: [any];
  recipientName?: string | null;
  recipientContactNumber?: string | null;
  shippingAddress?: string | null;
  shippingAddressDetail?: string | null;
  shippingPostalCode?: string | null;
  shippingMemo?: string | null;
};

export type OrderStatus =
  | "pending_payment"
  | "accepted"
  | "resubmission"
  | "production"
  | "production_complete"
  | "shipping"
  | "cancelled";


const ShippingManagement = () => {
  const { companyId } = useParams();

  const repo = useMemo(() => new CompanyOrderRepositoryImpl(), []);
  const usecase = useMemo(() => new CompanyOrderUsecase(repo), [repo]);

  const [orders, setOrders] = useState<Order[]>([]);
  const [shippedOrder, setShippedOrder] = useState<Order[]>([]);
  const [completeOrder, setCompleteOrder] = useState<Order[]>([]);

  const [isTrackingOpen, setisTrackingOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [deliveryCompany, setDeliveryCompany] = useState("");
  const [courierOptions, setCourierOptions] = useState<{ value: string; name: string }[]>([
    { value: "", name: "택배사를 선택하세요" },
  ]);
  const [trackingResult, setTrackingResult] = useState<{
    orderNumber: string | null;
    productName: string;
    courier: string | null;
    trackingNumber: string | null;
    trackingHistory: any[];
    lastFetchedAt: string | null;
    remainingManualRefresh: number;
  } | null>(null);

  // 수동 새로고침: 택배사 조회 한도를 쓰는 유일한 사용자 동작이라 확인 단계를 둔다
  const [isRefreshConfirmOpen, setIsRefreshConfirmOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // 송장 등록 모달을 수정 용도로 열었는지
  const [isTrackingEdit, setIsTrackingEdit] = useState(false);
  // 배송완료 주문을 열었는지. 이 경우 갱신할 배송 정보가 더 없으므로 새로고침을 숨긴다
  const [isDeliveredView, setIsDeliveredView] = useState(false);

  // 일괄 출고등록 관련 state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkShipmentRegisterResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    try {
      const blob = await usecase.downloadShippingTemplate(cid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shipping_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[ShippingManagement] downloadTemplate failed:", e);
      alert("템플릿 다운로드에 실패했습니다.");
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      alert("엑셀 파일을 선택해주세요.");
      return;
    }

    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    setBulkUploading(true);
    try {
      const result = await usecase.bulkShipmentRegister(cid, bulkFile);
      setBulkResult(result);

      // 성공 시 목록 갱신
      const orderResult = await usecase.getOrders(cid, "PRODUCTION_COMPLETED");
      setOrders(orderResult.items.map(mapApiOrderToView));

      const shippedResult = await usecase.getOrders(cid, "SHIPPED");
      setShippedOrder(shippedResult.items.map(mapApiOrderToView));

      const completeResult = await usecase.getOrders(cid, "DELIVERY_COMPLETED");
      setCompleteOrder(completeResult.items.map(mapApiOrderToView));
    } catch (e: any) {
      console.error("[ShippingManagement] bulkUpload failed:", e);
      alert(e.message || "일괄 출고등록에 실패했습니다.");
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkModalClose = () => {
    setIsBulkModalOpen(false);
    setBulkFile(null);
    setBulkResult(null);
  };

  const handleRegisterTracking = async (order: Order, mode: "create" | "edit" = "create") => {
    setSelectedOrder(order);
    setIsTrackingEdit(mode === "edit");
    // 수정으로 열 때는 기존 값을 채워 둔다. 빈 칸으로 열면 무엇을 고치는지 알 수 없다.
    setTrackingNumber(mode === "edit" ? order.trackingNumber ?? "" : "");
    setDeliveryCompany(mode === "edit" ? order.courier ?? "" : "");

    try {
      // 택배사 api 호출
      const result = await usecase.getCourierList();

      const list = Array.isArray(result) ? result : [];

      const options = [
        { value: "", name: "택배사를 선택하세요" },
        ...list.map((item) => ({
          value: item.name,  // 백엔드 Courier ENUM 이름 (e.g. "COURIER_04")
          name: item.title,  // 화면에 보여줄 이름 (e.g. "CJ대한통운")
        })),
      ];

      setCourierOptions(options);
    } catch (e) {
      console.error("[ShippingManagement] getCourierList failed:", e);
      setCourierOptions([{ value: "", name: "택배사를 선택하세요" }]);
    }

    setisTrackingOpen(true);
  };

  const handleViewTracking = async (order: Order, delivered = false) => {
    setSelectedOrder(order);
    setIsDeliveredView(delivered);

    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    if (!order.trackingNumber || !order.courier) {
      alert("송장번호 또는 택배사 정보가 없습니다.");
      return;
    }

    try {
      const result = await usecase.trackingOrder(cid, order.id, order.trackingNumber, order.courier);
      // usecase.trackingOrder 는 ApiResponse<T> 를 반환하므로, 실제 데이터는 result.data 에 있음
      setTrackingResult(result.data);
      setIsDetailModalOpen(true);
    } catch (e) {
      console.error("[ShippingManagement] trackingOrder failed:", e);
      alert("배송 조회에 실패했습니다.");
    }
  };

  /**
   * 수동 새로고침 실행.
   *
   * 화면 열람은 저장된 이력을 읽을 뿐이고, 택배사 API 를 실제로 부르는 건 이 동작뿐이다.
   * 송장당 하루 사용 횟수가 정해져 있어 서버가 초과를 거절한다.
   */
  const handleRefreshTracking = async () => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid) || !selectedOrder) return;

    setIsRefreshing(true);
    try {
      const result = await usecase.refreshTracking(cid, selectedOrder.id);
      setTrackingResult(result.data);
      setIsRefreshConfirmOpen(false);
    } catch (e: any) {
      console.error("[ShippingManagement] refreshTracking failed:", e);
      alert(e?.message ?? "배송 정보를 갱신하지 못했습니다.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleViewTrackingClose = async () => {
    setIsDetailModalOpen(false);
    setTrackingResult(null);

    // 제작 완료 목록 불러오기
    try {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;

      const result = await usecase.getOrders(cid, "PRODUCTION_COMPLETED");
      const mapped = result.items.map(mapApiOrderToView);
      setOrders(mapped);

    } catch (e: any) {
      console.error(e);
      alert("제작 완료 리스트를 불러오지 못했습니다.");
    }

    // 출고/배송 목록 불러오기
    try {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;

      const result = await usecase.getOrders(cid, "SHIPPED");
      const mapped = result.items.map(mapApiOrderToView);

      console.log(mapped);
      setShippedOrder(mapped);

    } catch (e: any) {
      console.error(e);
      alert("출고/배송 리스트를 불러오지 못했습니다.");
    }

    // 배송 완료 목록 불러오기
    try {
      const cid = Number(companyId);
      if (!Number.isFinite(cid)) return;

      const result = await usecase.getOrders(cid, "DELIVERY_COMPLETED");
      const mapped = result.items.map(mapApiOrderToView);
      setCompleteOrder(mapped);

    } catch (e: any) {
      console.error(e);
      alert("출고/배송 리스트를 불러오지 못했습니다.");
    }
  }

  const handleSaveTracking = async () => {
    if (!trackingNumber || !deliveryCompany) {
      alert("택배사와 송장번호를 모두 입력해주세요");
      return;
    }

    if (!selectedOrder) {
      alert("주문을 선택해 주세요.");
    }

    // 롯데 08
    // 운송장 번호 249457469391

    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    // 서버가 거절하면(같은 송장이 다른 주문에 이미 있음 등) 예외로 온다. 잡지 않으면 아무 안내 없이 모달만 남는다.
    let result;
    try {
      result = await usecase.updateTrackingInfo(cid, selectedOrder!.id, deliveryCompany, trackingNumber);
    } catch (e: any) {
      console.error("[ShippingManagement] updateTrackingInfo failed:", e);
      alert(
        e?.originalError?.message ??
          (isTrackingEdit ? "송장정보 수정에 실패하였습니다." : "배송정보 등록에 실패하였습니다."),
      );
      return;
    }

    if (result.message) {
      console.log(result);
      alert(isTrackingEdit ? "송장정보 수정에 실패하였습니다." : "배송정보 등록에 실패하였습니다.");
      return;
    }

    setisTrackingOpen(false);
    setIsTrackingEdit(false);
    setTrackingNumber("");
    setDeliveryCompany("");


    const orderResult = await usecase.getOrders(cid, "PRODUCTION_COMPLETED");
    const mapped = orderResult.items.map(mapApiOrderToView);
    setOrders(mapped);

    const shippedResult = await usecase.getOrders(cid, "SHIPPED");
    const shippedMapped = shippedResult.items.map(mapApiOrderToView);
    setShippedOrder(shippedMapped);

    const completeResult = await usecase.getOrders(cid, "DELIVERY_COMPLETED");
    const completeMapped = completeResult.items.map(mapApiOrderToView);
    setCompleteOrder(completeMapped);
  };

  useEffect(() => {
    (async () => {
      // 제작 완료 목록 불러오기
      try {
        const cid = Number(companyId);
        if (!Number.isFinite(cid)) return;

        const result = await usecase.getOrders(cid, "PRODUCTION_COMPLETED");
        const mapped = result.items.map(mapApiOrderToView);
        setOrders(mapped);

      } catch (e: any) {
        console.error(e);
        alert("제작 완료 리스트를 불러오지 못했습니다.");
      }

      // 출고/배송 목록 불러오기
      try {
        const cid = Number(companyId);
        if (!Number.isFinite(cid)) return;

        const result = await usecase.getOrders(cid, "SHIPPED");
        const mapped = result.items.map(mapApiOrderToView);

        setShippedOrder(mapped);

      } catch (e: any) {
        console.error(e);
        alert("출고/배송 리스트를 불러오지 못했습니다.");
      }

      // 배송 완료 목록 불러오기
      try {
        const cid = Number(companyId);
        if (!Number.isFinite(cid)) return;

        const result = await usecase.getOrders(cid, "DELIVERY_COMPLETED");
        const mapped = result.items.map(mapApiOrderToView);
        setCompleteOrder(mapped);

      } catch (e: any) {
        console.error(e);
        alert("출고/배송 리스트를 불러오지 못했습니다.");
      }
    })()
  }, [companyId]);

  const apiStatusToUiStatus = (status: string): OrderStatus => {
    console.log(status);
    switch (status) {
      case "BEFORE_PAYMENT":
        return "pending_payment";
      case "RECEIVED":
        return "accepted";
      case "RE_RECEIVED":
        return "resubmission";
      case "IN_PRODUCTION":
        return "production";
      case "PRODUCTION_COMPLETED":
        return "production_complete";
      case "SHIPPED":
        return "shipping";
      case "REFUNDED":
        return "cancelled"; // 프론트의 취소/환불 합침 상태로 임시 매핑
      default:
        return "accepted"; // 기본값(안전)
    }
  };

  const mapApiOrderToView = (o: any): Order => ({
    id: Number(o.id),
    orderNumber: String(o.orderNumber ?? o.id),
    customerName: o.purchaserName,
    customerEmail: o.purchaserEmail,
    productName: o.name, // 서버 요약에 상품명이 없다면 백엔드 보완 전까지 주문명으로 대체
    quantity: o.productCount ?? 0,
    unitPrice: o.productPrice ?? 0,
    totalAmount: o.orderTotalAmount ?? 0,
    status: apiStatusToUiStatus(o.orderStatus),
    orderDate: (o.createdAt ?? "").toString().slice(0, 10),
    updatedAt: (o.updatedAt ?? "").toString().slice(0, 10),
    memo: o.memo, // 요약 응답에 없으므로 빈 값
    optionsText: Array.isArray(o.options) ? o.options : [],
    files: o.orderSourceFile, // 요약 응답에서는 파일 정보 없음
    trackingHistory: Array.isArray(o.trackingHistory) ? o.trackingHistory : [],
    trackingNumber: o.trackingNumber,
    courier: o.courier,
    recipientName: o.recipientName,
    recipientContactNumber: o.recipientContactNumber,
    shippingAddress: o.shippingAddress,
    shippingAddressDetail: o.shippingAddressDetail,
    shippingPostalCode: o.shippingPostalCode,
    shippingMemo: o.shippingMemo,
  });

  useEffect(() => {
    (async () => {
      if (!isDetailModalOpen) {
        // 제작 완료 목록 불러오기
        try {
          const cid = Number(companyId);
          if (!Number.isFinite(cid)) return;

          const result = await usecase.getOrders(cid, "PRODUCTION_COMPLETED");
          const mapped = result.items.map(mapApiOrderToView);
          setOrders(mapped);

        } catch (e: any) {
          console.error(e);
          alert("제작 완료 리스트를 불러오지 못했습니다.");
        }

        // 출고/배송 목록 불러오기
        try {
          const cid = Number(companyId);
          if (!Number.isFinite(cid)) return;

          const result = await usecase.getOrders(cid, "SHIPPED");
          const mapped = result.items.map(mapApiOrderToView);
          setShippedOrder(mapped);

        } catch (e: any) {
          console.error(e);
          alert("출고/배송 리스트를 불러오지 못했습니다.");
        }

        // 배송 완료 목록 불러오기
        try {
          const cid = Number(companyId);
          if (!Number.isFinite(cid)) return;

          const result = await usecase.getOrders(cid, "DELIVERY_COMPLETED");
          const mapped = result.items.map(mapApiOrderToView);
          console.log(mapped);
          setCompleteOrder(mapped);

        } catch (e: any) {
          console.error(e);
          alert("출고/배송 리스트를 불러오지 못했습니다.");
        }
      }

    })()
  }, [isDetailModalOpen]);

  return (
    <>
      {/* Bulk Shipment Registration Modal */}
      {isBulkModalOpen && (
        <Modal setIsModalVisible={setIsBulkModalOpen} title="일괄 출고등록">
          <>
            <div className="w-[70vw] max-w-[550px]"></div>

            <div className="space-y-4 mt-[20px]">
              {/* 템플릿 다운로드 */}
              <div className="p-4 bg-gray-100 rounded-[0.4rem]">
                <p className="text-sm text-gray-700 mb-2 font-medium">1. 엑셀 템플릿을 다운로드하세요</p>
                <p className="text-xs text-gray-500 mb-3">제작 완료된 주문이 미리 입력되어 있습니다. 택배사와 송장번호를 입력 후 업로드하세요.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  템플릿 다운로드 (.xlsx)
                </Button>
              </div>

              {/* 파일 업로드 */}
              <div className="p-4 bg-gray-100 rounded-[0.4rem]">
                <p className="text-sm text-gray-700 mb-2 font-medium">2. 작성한 엑셀 파일을 업로드하세요</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setBulkFile(file);
                  }}
                />
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    파일 선택
                  </Button>
                  <span className="text-xs text-gray-500">
                    {bulkFile ? bulkFile.name : "선택된 파일 없음"}
                  </span>
                </div>
              </div>

              {/* 업로드 결과 */}
              {bulkResult && (
                <div className="p-4 rounded-[0.4rem] border border-gray-200">
                  <p className="text-sm font-medium mb-2">업로드 결과</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">전체</p>
                      <p className="text-lg font-bold">{bulkResult.totalCount}</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <p className="text-xs text-green-600">성공</p>
                      <p className="text-lg font-bold text-green-600">{bulkResult.successCount}</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                      <p className="text-xs text-red-600">실패</p>
                      <p className="text-lg font-bold text-red-600">{bulkResult.failCount}</p>
                    </div>
                  </div>

                  {bulkResult.failures.length > 0 && (
                    <div className="max-h-[150px] overflow-auto">
                      <p className="text-xs font-medium text-red-600 mb-1">실패 상세</p>
                      {bulkResult.failures.map((f, idx) => (
                        <div key={idx} className="text-xs text-red-500 py-1 border-b border-gray-100">
                          행 {f.rowNumber}{f.orderNumber ? ` (${f.orderNumber})` : ""}: {f.reason}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={handleBulkModalClose}>
                {bulkResult ? "닫기" : "취소"}
              </Button>
              {!bulkResult && (
                <Button onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}>
                  {bulkUploading ? "업로드 중..." : "일괄 등록"}
                </Button>
              )}
            </div>
          </>
        </Modal>
      )}

      {isDetailModalOpen && selectedOrder && (
        <>
          <Modal setIsModalVisible={setIsDetailModalOpen} title="배송 현황">
            <>
              <div className="w-[70vw] max-w-[500px]"></div>
              {selectedOrder && (
                <div className="mb-4 p-3 bg-gray-100 rounded-[0.4rem] mt-[20px]">
                  <div className="grid grid-cols-2 gap-4 p-4  rounded">
                    <div>
                      <p className="text-sm text-gray-600">주문번호</p>
                      <p className="font-medium">
                        {trackingResult?.orderNumber ?? selectedOrder.orderNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">상품명</p>
                      <p className="font-medium">
                        {trackingResult?.productName ?? selectedOrder.productName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">택배사</p>
                      <p className="font-medium">
                        {trackingResult?.courier
                          ? COURIER_LABEL_MAP[trackingResult.courier] ?? trackingResult.courier
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">송장번호</p>
                      <p className="font-medium text-blue-600">
                        {trackingResult?.trackingNumber ?? selectedOrder.trackingNumber ?? "-"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] bg-[#E8EFFF] text-[#4285F4] text-[11px] font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4285F4]"></span>
                  {isDeliveredView ? "배송 완료" : "자동 추적 중"}
                </span>
                {!isDeliveredView && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(trackingResult?.remainingManualRefresh ?? 0) <= 0}
                    onClick={() => setIsRefreshConfirmOpen(true)}
                    className="flex items-center"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    새로고침
                  </Button>
                )}
              </div>

              <div style={{ height: "300px", overflow: "auto" }}>
                <h3 className="font-medium mb-3">배송 현황</h3>
                <div className="space-y-3">
                  {trackingResult && trackingResult.trackingHistory.length > 0 ? (
                    trackingResult.trackingHistory.map((h: any, idx: number) => {
                      const level = Number(h.level ?? 0);
                      const dotColor =
                        level >= 6
                          ? "bg-green-500"
                          : level >= 5
                            ? "bg-blue-500"
                            : "bg-gray-300";
                      const timeText = (h.time ?? "").toString().replace("T", " ").slice(0, 16);
                      return (
                        <div
                          key={idx}
                          className="flex items-center space-x-3 p-3 border rounded-[0.4rem]"
                        >
                          <div className={`w-3 h-3 rounded-full ${dotColor}`}></div>
                          <div className="flex-1">
                            <p className="font-bold">{h.description ?? "-"}</p>
                            <p className="text-[0.8rem] text-gray-600 font-medium">
                              {timeText}
                              {h.currentLocation ? ` - ${h.currentLocation}` : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-[0.8rem] text-gray-500">배송 이력이 없습니다.</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-6">
                <p className="text-[12px] text-[#888]">
                  {trackingResult?.lastFetchedAt
                    ? `${trackingResult.lastFetchedAt.toString().replace("T", " ").slice(0, 16)} 기준`
                    : "아직 수집된 배송 정보가 없습니다"}
                  {(trackingResult?.remainingManualRefresh ?? 0) <= 0 && (
                    <span className="block mt-1 text-[#4285F4]">
                      오늘 수동 조회 횟수를 모두 사용했습니다. 배송 정보는 자동으로 계속 수집됩니다.
                    </span>
                  )}
                </p>
                <Button
                  variant="outline"
                  onClick={handleViewTrackingClose}
                >
                  닫기
                </Button>
              </div>
            </>
          </Modal>
        </>
      )}

      {isRefreshConfirmOpen && (
        <Modal setIsModalVisible={setIsRefreshConfirmOpen} title="배송 정보를 지금 갱신할까요?">
          <>
            <div className="w-[70vw] max-w-[380px]"></div>

            <div className="mt-[20px] p-4 rounded-[8px] bg-[#EBF3FF] border border-[#4285F4]/20 text-start">
              <p className="text-[13px] text-[#4285F4] font-medium">● 이 주문은 자동 추적 중입니다</p>
              <p className="text-[13px] text-[#4285F4] mt-1 leading-relaxed">
                배송 정보는 자동으로 수집되고 있습니다.
              </p>
            </div>

            <div className="mt-3 text-start">
              <p className="text-[12px] text-[#555]">오늘 수동 조회 가능 횟수</p>
              <p className="text-[14px] text-[#222] font-medium mt-1">
                {trackingResult?.remainingManualRefresh ?? 0}회 남았습니다
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setIsRefreshConfirmOpen(false)}>
                취소
              </Button>
              <Button onClick={handleRefreshTracking} disabled={isRefreshing}>
                {isRefreshing ? "갱신 중..." : "갱신하기"}
              </Button>
            </div>
          </>
        </Modal>
      )}

      {/* Tracking Number Registration Modal */}
      {isTrackingOpen && (
        <>
          <Modal setIsModalVisible={setisTrackingOpen} title={isTrackingEdit ? "송장정보 수정" : "배송정보 등록"}>
            <>
              <div className="w-[70vw] max-w-[500px]"></div>

              {selectedOrder && (
                <>
                  <div className="mb-4 p-3 bg-gray-200 rounded-[0.4rem] mt-[20px]">
                    <p className="text-sm text-gray-600">
                      주문번호: {selectedOrder.orderNumber}
                    </p>
                    <p className="text-sm text-gray-600">
                      상품명: {selectedOrder.productName}
                    </p>
                  </div>

                  {/* 송장을 쓰는 순간 받는 사람 정보가 가장 필요하다. 매번 주문 상세로 넘어가지 않도록 여기 띄운다 */}
                  <div className="mb-4 p-4 bg-white border border-[#e0e0e0] rounded-[8px] text-start">
                    <p className="text-[12px] font-medium text-[#555] mb-2">배송 정보</p>
                    <div className="space-y-1.5">
                      <div className="flex gap-2">
                        <span className="text-[13px] text-[#888] w-[60px] shrink-0">수령인</span>
                        <span className="text-[13px] text-[#222]">
                          {selectedOrder.recipientName || "미입력"}
                          {selectedOrder.recipientContactNumber ? ` · ${selectedOrder.recipientContactNumber}` : ""}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-[13px] text-[#888] w-[60px] shrink-0">주소</span>
                        <span className="text-[13px] text-[#222]">
                          {selectedOrder.shippingAddress
                            ? `${selectedOrder.shippingPostalCode ? `(${selectedOrder.shippingPostalCode}) ` : ""}${selectedOrder.shippingAddress}${selectedOrder.shippingAddressDetail ? ` ${selectedOrder.shippingAddressDetail}` : ""}`
                            : "미입력"}
                        </span>
                      </div>
                      {selectedOrder.shippingMemo && (
                        <div className="flex gap-2">
                          <span className="text-[13px] text-[#888] w-[60px] shrink-0">요청사항</span>
                          <span className="text-[13px] text-[#222]">{selectedOrder.shippingMemo}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-start font-medium mb-2">
                    택배사
                  </label>
                  <div className="h-10">
                    <Selection
                      selectedOption={deliveryCompany}
                      setSelectedOption={setDeliveryCompany}
                      options={courierOptions}
                    />
                  </div>

                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-start">
                    송장번호
                  </label>
                  <TextInput
                    placeholder="송장번호를 입력하세요"
                    value={trackingNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTrackingNumber(e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setisTrackingOpen(false)}
                >
                  취소
                </Button>
                <Button onClick={handleSaveTracking}>{isTrackingEdit ? "수정" : "등록"}</Button>
              </div>
            </>
          </Modal>
        </>
      )}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] font-semibold">배송 관리</h2>
            <div className="flex flex-col md:flex-row justify-between gap-5 w-full">
              <span className="text-start w-full font-normal">
                제작 완료 및 배송 중인 주문을 관리하세요
              </span>
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-[12px] px-3 py-1.5 border border-[#d9d9d9] rounded-[4px] bg-white font-medium flex items-center">
                  <Package className="w-3 h-3 mr-1" />
                  제작 완료: {orders.length}건
                </span>
                <span className="whitespace-nowrap text-[12px] px-3 py-1.5 border border-[#d9d9d9] rounded-[4px] bg-white font-medium flex items-center">
                  <Truck className="w-3 h-3 mr-1" />
                  {/* 배송중: {shippingOrders.length}건 */}
                </span>
                <span className="whitespace-nowrap text-[12px] px-3 py-1.5 border border-[#d9d9d9] rounded-[4px] bg-white font-medium flex items-center">
                  <Truck className="w-3 h-3 mr-1" />
                  {/* 배송 완료: {shippingOrders.length}건 */}
                </span>
              </div>
            </div>
          </div>

          <div className="text-[13px] space-y-6">
            {/* Production Complete Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[14px] font-medium text-[#252525] flex items-center">
                  <Package className="w-4 h-4 mr-2 text-[#4285f4]" />
                  제작 완료 ({orders.length})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsBulkModalOpen(true)}
                  className="flex items-center"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  일괄 출고등록
                </Button>
              </div>
              <div className="text-[12px]">
                <Table
                  tabledata={{
                    head: [
                      <>주문번호</>,
                      <>주문자</>,
                      <>상품명</>,
                      <>수량</>,
                      <>완료일</>,
                      <>작업</>,
                    ],
                    body: orders.map((item, index) => {
                      return [
                        <>{item.orderNumber}</>,
                        <>{item.customerName}</>,
                        <>{item.productName}</>,
                        <>{item.quantity}개</>,
                        <>{item.updatedAt}</>,
                        <div className="flex flex-row gap-[8px]">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRegisterTracking(item)}
                            className="flex items-center"
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            배송등록
                          </Button>
                        </div>,
                      ];
                    }),
                  }}
                />
              </div>
            </div>

            {/* Shipping Section */}
            <div>
              <h3 className="text-[14px] font-medium text-[#252525] mb-3 flex items-center">
                <Truck className="w-4 h-4 mr-2 text-[#4285f4]" />
                출고/배송 ({shippedOrder.length})
              </h3>
              <div className="text-[12px]">
                <Table
                  tabledata={{
                    head: [
                      <>주문번호</>,
                      <>수령인</>,
                      <>상품명</>,
                      <>수량</>,
                      <>출고일</>,
                      <>택배사</>,
                      <>송장번호</>,
                      <>배송추적</>,
                      <>송장수정</>,
                    ],
                    body: shippedOrder.map((item, index) => {
                      return [
                        <>{item.orderNumber}</>,
                        <>{item.recipientName || "-"}</>,
                        <>{item.productName}</>,
                        <>{item.quantity}개</>,
                        <>
                          {Array.isArray(item.trackingHistory) && item.trackingHistory.length > 0
                            ? (item.trackingHistory[0].time ?? "").toString().replace("T", " ").slice(0, 16)
                            : "-"}
                        </>,
                        <>
                          {item.courier
                            ? COURIER_LABEL_MAP[item.courier] ?? item.courier
                            : "-"}
                        </>,
                        <>{item.trackingNumber}</>,
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewTracking(item)}
                            className="flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            추적
                          </Button>
                        </>,
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRegisterTracking(item, "edit")}
                            className="flex items-center"
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            수정
                          </Button>
                        </>,
                      ];
                    }),
                  }}
                />
              </div>
            </div>

            {/* Shipping Success Section */}
            <div>
              <h3 className="text-[14px] font-medium text-[#252525] mb-3 flex items-center">
                <Truck className="w-4 h-4 mr-2 text-[#4285f4]" />
                배송 완료 ({completeOrder.length})
              </h3>
              <div className="text-[12px]">
                <Table
                  tabledata={{
                    head: [
                      <>주문번호</>,
                      <>수령인</>,
                      <>상품명</>,
                      <>수량</>,
                      <>출고일</>,
                      <>배송완료일</>,
                      <>택배사</>,
                      <>송장번호</>,
                      <>배송추적</>,
                    ],
                    body: completeOrder.map((item, index) => {
                      return [
                        <>{item.orderNumber}</>,
                        <>{item.recipientName || "-"}</>,
                        <>{item.productName}</>,
                        <>{item.quantity}개</>,
                        <>
                          {Array.isArray(item.trackingHistory) && item.trackingHistory.length > 0 && item.trackingHistory[0]?.time
                            ? item.trackingHistory[0].time.toString().replace("T", " ").slice(0, 16)
                            : "-"}
                        </>,
                        <>
                          {Array.isArray(item.trackingHistory) && item.trackingHistory.length > 0
                            ? (item.trackingHistory[item.trackingHistory.length - 1].time ?? "")
                                .toString()
                                .replace("T", " ")
                                .slice(0, 16)
                            : "-"}
                        </>,
                        <>{item.courier
                          ? COURIER_LABEL_MAP[item.courier] ?? item.courier
                          : "-"}</>,
                        <>{item.trackingNumber}</>,
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewTracking(item, true)}
                            className="flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            추적
                          </Button>
                        </>,
                      ];
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default ShippingManagement;
